// @flow

import {makeStyles, ButtonBase} from '@material-ui/core';
import React, {useState} from 'react';
import FunnelColumn from '../FunnelColumn/FunnelColumn';
import {colors} from '../../../../../../assets/styles/colors';
import {
  useCurrentMultichatFunnel,
  useCurrentMultichatFunnelActions,
} from '../../../utils';
import {Add} from '@material-ui/icons';
import FunnelLaneFormModal from '../../Modals/FunnelLaneFormModal';
import {useToggle} from '../../../../../../utils/react';
import {Droppable, Draggable} from 'react-beautiful-dnd';

import type {MultichatFunnelStage} from '../../../../../../entities';
import {useFeatureSpec} from '../../../../../../utils/component';
import ChatModal from '../../Modals/ChatModal';
import ConfirmModal from '../../../../../../elements/Modals/ConfirmModal';

const useStyles = makeStyles(() => ({
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    overflow: 'auto',
    gap: '.5rem',
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },

    '&::-webkit-scrollbar-track': {
      background: colors.grey200,
      borderRadius: '5px',
    },

    '&::-webkit-scrollbar-thumb': {
      background: colors.grey400,
      borderRadius: '5px',
    },
  },
  columnContainer: {
    borderRadius: 12,
    width: 350,
    flex: '350px 0 0',
  },
  columnButton: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '.5rem',
    fontSize: 14,
    borderRadius: 12,
    backgroundColor: '#EAEBF1',
    fontWeight: 600,
    padding: '1rem',
    color: colors.grey700,
    cursor: 'pointer',
  },
}));

const FunnelColumnList = ({
  draggingType,
  search,
}: {
  draggingType: string | null,
  search?: string,
}) => {
  const classes = useStyles();
  const currentFunnel = useCurrentMultichatFunnel();

  const featureSpec = useFeatureSpec('FUNNEL');
  const {deleteFunnelLane} = useCurrentMultichatFunnelActions();
  const [createModalOpen, toggleCreateModalOpen] = useToggle();
  const [stageToEdit, setStageToEdit] = useState<MultichatFunnelStage | null>(
    null
  );
  const [lineIdToDelete, setLineIdToDelete] = useState<string | null>(null);

  const maxLanes = featureSpec?.max_lanes_per_funnel || 10;

  if (!currentFunnel) return null;

  const handleDelete = (laneId: string) => {
    setLineIdToDelete(laneId);
    setStageToEdit(null);
  };

  return (
    <div className={classes.container}>
      <Droppable droppableId="all-columns" direction="horizontal" type="COLUMN">
        {(provided, snapshot) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            style={{
              display: 'flex',
              gap: '.5rem',
              height: '100%',
              padding: '.25rem',
              borderRadius: 12,
              transition: 'background-color 0.2s ease',
              backgroundColor: snapshot.isDraggingOver
                ? 'rgba(23, 116, 255, 0.1)'
                : draggingType === 'COLUMN'
                ? 'rgba(23, 116, 255, 0.05)'
                : 'transparent',
              border:
                draggingType === 'COLUMN'
                  ? '2px dashed rgba(23, 116, 255, 0.3)'
                  : '2px solid transparent',
            }}
          >
            {currentFunnel.lanes.map((lane, index) => (
              <Draggable key={lane.id} draggableId={lane.id} index={index}>
                {provided => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    style={{...provided.draggableProps.style, height: '100%'}}
                  >
                    <FunnelColumn
                      lane={lane}
                      dragHandleProps={provided.dragHandleProps}
                      onEdit={() => setStageToEdit(lane.stage)}
                      onDelete={() => handleDelete(lane.id)}
                      isDraggingCard={draggingType === 'DEFAULT'}
                      search={search}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
            {maxLanes > currentFunnel.lanes.length && (
              <div className={classes.columnContainer}>
                <ButtonBase
                  className={classes.columnButton}
                  onClick={toggleCreateModalOpen}
                >
                  <Add />
                  <span>Agregar nueva lista</span>
                </ButtonBase>
              </div>
            )}
          </div>
        )}
      </Droppable>

      <FunnelLaneFormModal
        open={createModalOpen}
        onClose={toggleCreateModalOpen}
      />

      {stageToEdit && (
        <FunnelLaneFormModal
          open={!!stageToEdit}
          stage={stageToEdit}
          onClose={() => setStageToEdit(null)}
        />
      )}
      <ChatModal />

      <ConfirmModal
        open={!!lineIdToDelete}
        message="Esta lista se eliminará permanentemente."
        onCancel={() => setLineIdToDelete(null)}
        onClose={() => setLineIdToDelete(null)}
        onConfirm={() => {
          if (!lineIdToDelete) return;
          deleteFunnelLane(lineIdToDelete).then(() => {
            setLineIdToDelete(null);
          });
        }}
      />
    </div>
  );
};

export default FunnelColumnList;
